import { useState, useEffect } from 'react'
import { useAuthStore } from '../hooks/useAuthStore'
import { Link } from 'react-router-dom'

/* ── Design tokens ──────────────────────────────────────── */
const C = {
  primary:            '#4800b2',
  primaryContainer:   '#6200ee',
  onPrimary:          '#ffffff',
  primaryFixed:       '#e8ddff',
  onPrimaryFixed:     '#22005d',
  surface:            '#f8f9fd',
  surfaceContainerLowest: '#ffffff',
  surfaceContainerLow:    '#f2f3f7',
  surfaceContainerHigh:   '#e7e8ec',
  secondaryFixed:     '#e3e0f4',
  onSurface:          '#191c1f',
  onSurfaceVariant:   '#494456',
  outline:            '#7a7488',
  outlineVariant:     '#cbc3d9',
  secondary:          '#5e5d6d',
  error:              '#ba1a1a',
}

/* ── Reusable field component ────────────────────────────── */
function Field({ label, icon, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <label style={{
        fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
        letterSpacing: 1.4, color: C.onSurfaceVariant, marginLeft: 4,
      }}>{label}</label>
      <div style={{ position: 'relative' }}>
        {children}
        {icon && (
          <i className={`uil ${icon}`} style={{
            position: 'absolute', right: 14, top: '50%',
            transform: 'translateY(-50%)',
            fontSize: 16, color: C.outline, pointerEvents: 'none',
          }} />
        )}
      </div>
    </div>
  )
}

const inputStyle = (disabled = false) => ({
  width: '100%',
  background: disabled ? C.surfaceContainerLow : C.surfaceContainerHigh,
  border: 'none',
  borderRadius: 12,
  padding: '12px 16px',
  fontSize: 14,
  fontWeight: 500,
  color: disabled ? C.outline : C.onSurface,
  outline: 'none',
  boxSizing: 'border-box',
  cursor: disabled ? 'not-allowed' : 'text',
})

export default function Profile() {
  const { token, user, isAuthenticated } = useAuthStore()
  const [isLoading, setIsLoading]   = useState(false)
  const [error, setError]           = useState('')
  const [success, setSuccess]       = useState('')
  const [activeTab, setActiveTab]   = useState('profile')

  const [profileData, setProfileData] = useState({
    firstName: '', lastName: '', email: '',
    role: '', specialisation: '', licenseNumber: '', phone: '',
  })
  const [appAccess, setAppAccess] = useState([])

  const fetchProfile = async () => {
    setIsLoading(true)
    try {
      const r = await fetch('/api/users/profile', { headers: { Authorization: `Bearer ${token}` } })
      if (r.ok) {
        const d = await r.json()
        setProfileData({
          firstName:      d.user.firstName,
          lastName:       d.user.lastName,
          email:          d.user.email,
          role:           d.user.role,
          specialisation: d.user.specialisation || '',
          licenseNumber:  d.user.licenseNumber || '',
          phone:          d.user.phone || '',
        })
      } else setError('Failed to load profile')
    } catch { setError('Error loading profile') }
    finally { setIsLoading(false) }
  }

  const fetchAppAccess = async () => {
    try {
      const r = await fetch('/api/users/app-access', { headers: { Authorization: `Bearer ${token}` } })
      if (r.ok) { const d = await r.json(); setAppAccess(d.appAccess || []) }
    } catch {}
  }

  const handleProfileSubmit = async (e) => {
    e.preventDefault()
    setIsLoading(true); setError(''); setSuccess('')
    try {
      const r = await fetch('/api/users/profile', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          firstName:      profileData.firstName,
          lastName:       profileData.lastName,
          specialisation: profileData.specialisation,
          licenseNumber:  profileData.licenseNumber,
          phone:          profileData.phone,
        }),
      })
      const d = await r.json()
      if (r.ok) { setSuccess('Profile updated successfully'); setTimeout(() => setSuccess(''), 3000) }
      else setError(d.error || 'Failed to update profile')
    } catch { setError('Error updating profile') }
    finally { setIsLoading(false) }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setProfileData(prev => ({ ...prev, [name]: value }))
  }

  const handleReset = () => fetchProfile()

  useEffect(() => {
    if (token && isAuthenticated) { fetchProfile(); fetchAppAccess() }
  }, [token, isAuthenticated])

  if (!isAuthenticated) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300 }}>
        <p style={{ color: C.onSurfaceVariant, fontSize: 15 }}>Please log in to view your profile.</p>
      </div>
    )
  }

  const TABS = [
    { id: 'profile', icon: 'uil-user', label: 'Personal Info' },
    { id: 'apps',    icon: 'uil-apps', label: 'App Access' },
  ]

  return (
    <div style={{
      maxWidth: 900, margin: '0 auto',
      fontFamily: "'Inter','Segoe UI',sans-serif",
      color: C.onSurface,
    }}>

      {/* ── Page header ─────────────────────────────────────── */}
      <div style={{
        display: 'flex', flexWrap: 'wrap',
        alignItems: 'flex-end', justifyContent: 'space-between',
        gap: 20, marginBottom: 28,
      }}>
        <div>
          <h2 style={{
            fontSize: 24, fontWeight: 800, margin: '0 0 6px',
            fontFamily: "'Manrope','Inter',sans-serif",
            color: C.onSurface, letterSpacing: '-0.4px',
          }}>My Health Profile</h2>
          <p style={{ fontSize: 13, color: C.onSurfaceVariant, margin: 0, maxWidth: 480, lineHeight: 1.6 }}>
            Manage your private medical information. This data is encrypted and only accessible by authorised clinical staff during consultations.
          </p>
        </div>

        {/* Role badge */}
        <div style={{
          background: `${C.primaryContainer}15`,
          border: `1px solid ${C.primaryContainer}22`,
          borderRadius: 14, padding: '12px 18px',
          display: 'flex', alignItems: 'center', gap: 14,
        }}>
          <div style={{
            background: C.primaryContainer, borderRadius: 10,
            width: 40, height: 40, display: 'flex', alignItems: 'center',
            justifyContent: 'center',
          }}>
            <i className="uil uil-shield-check" style={{ color: '#fff', fontSize: 20 }} />
          </div>
          <div>
            <div style={{ fontSize: 9, fontWeight: 700, color: C.primaryContainer, textTransform: 'uppercase', letterSpacing: 1.6 }}>
              Account Role
            </div>
            <div style={{ fontSize: 20, fontWeight: 800, color: C.primary, fontFamily: "'Manrope',sans-serif" }}>
              {(profileData.role || 'User').charAt(0).toUpperCase() + (profileData.role || 'User').slice(1)}
            </div>
          </div>
        </div>
      </div>

      {/* ── Alerts ──────────────────────────────────────────── */}
      {success && (
        <div style={{
          background: '#f0fdf4', border: '1px solid #bbf7d0',
          borderRadius: 12, padding: '12px 16px',
          display: 'flex', alignItems: 'center', gap: 10,
          marginBottom: 16, fontSize: 13, color: '#166534',
        }}>
          <i className="uil uil-check-circle" style={{ fontSize: 18 }} />
          {success}
        </div>
      )}
      {error && (
        <div style={{
          background: '#fef2f2', border: '1px solid #fecaca',
          borderRadius: 12, padding: '12px 16px',
          display: 'flex', alignItems: 'center', gap: 10,
          marginBottom: 16, fontSize: 13, color: C.error,
        }}>
          <i className="uil uil-exclamation-circle" style={{ fontSize: 18 }} />
          {error}
        </div>
      )}

      {/* ── Tab container ────────────────────────────────────── */}
      <div style={{ background: C.surfaceContainerLow, borderRadius: 16, padding: 4, overflow: 'hidden' }}>
        {/* Tab row */}
        <div style={{
          display: 'flex', gap: 4, padding: 4,
          background: C.surfaceContainerHigh,
          borderRadius: 12, overflowX: 'auto',
        }}>
          {TABS.map(t => {
            const active = activeTab === t.id
            return (
              <button key={t.id} onClick={() => setActiveTab(t.id)} style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '10px 20px',
                background: active ? C.surfaceContainerLowest : 'transparent',
                color: active ? C.primary : C.onSurfaceVariant,
                border: 'none', borderRadius: 10, cursor: 'pointer',
                fontWeight: active ? 700 : 500, fontSize: 13,
                whiteSpace: 'nowrap',
                boxShadow: active ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
                transition: 'all 0.15s',
                fontFamily: "'Inter',sans-serif",
              }}>
                <i className={`uil ${t.icon}`} style={{ fontSize: 15 }} />
                {t.label}
              </button>
            )
          })}
        </div>

        {/* ── PERSONAL INFO TAB ──────────────────────────────── */}
        {activeTab === 'profile' && (
          <form onSubmit={handleProfileSubmit} style={{
            background: C.surfaceContainerLowest,
            borderRadius: 12, marginTop: 4, padding: '32px 28px',
          }}>
            {isLoading && !profileData.firstName ? (
              <div style={{ textAlign: 'center', padding: '48px 0', color: C.outline }}>
                <i className="uil uil-spinner-alt" style={{ fontSize: 32, marginBottom: 8, display: 'block' }} />
                <p style={{ margin: 0, fontSize: 13 }}>Loading profile…</p>
              </div>
            ) : (
              <>
                {/* Section: Identity */}
                <div style={{ marginBottom: 32 }}>
                  <h3 style={{
                    fontSize: 15, fontWeight: 700,
                    fontFamily: "'Manrope',sans-serif",
                    color: C.onSurface, marginBottom: 20,
                    display: 'flex', alignItems: 'center', gap: 10,
                  }}>
                    <span style={{ width: 4, height: 24, background: C.primaryContainer, borderRadius: 4, flexShrink: 0 }} />
                    Identity &amp; Contact Information
                  </h3>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                    gap: '20px 28px',
                  }}>
                    <Field label="First Name" icon="uil-user">
                      <input
                        type="text" name="firstName" value={profileData.firstName}
                        onChange={handleInputChange} required
                        style={inputStyle()} placeholder="First name"
                      />
                    </Field>
                    <Field label="Last Name" icon="uil-user">
                      <input
                        type="text" name="lastName" value={profileData.lastName}
                        onChange={handleInputChange} required
                        style={inputStyle()} placeholder="Last name"
                      />
                    </Field>
                    <Field label="Email Address" icon="uil-envelope">
                      <input
                        type="email" name="email" value={profileData.email}
                        disabled style={{ ...inputStyle(true), paddingRight: 40 }}
                      />
                    </Field>
                    <Field label="Phone Number" icon="uil-phone">
                      <input
                        type="tel" name="phone" value={profileData.phone}
                        onChange={handleInputChange}
                        style={{ ...inputStyle(), paddingRight: 40 }}
                        placeholder="+1 (555) 000-0000"
                      />
                    </Field>
                  </div>
                </div>

                {/* Section: Professional */}
                <div style={{ marginBottom: 32 }}>
                  <h3 style={{
                    fontSize: 15, fontWeight: 700,
                    fontFamily: "'Manrope',sans-serif",
                    color: C.onSurface, marginBottom: 20,
                    display: 'flex', alignItems: 'center', gap: 10,
                  }}>
                    <span style={{ width: 4, height: 24, background: C.secondary, borderRadius: 4, flexShrink: 0 }} />
                    Professional Information
                  </h3>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                    gap: '20px 28px',
                  }}>
                    <Field label="Role" icon="uil-shield">
                      <input
                        type="text" name="role" value={profileData.role}
                        disabled style={{ ...inputStyle(true), paddingRight: 40 }}
                      />
                    </Field>
                    <Field label="Specialisation" icon="uil-stethoscope">
                      <input
                        type="text" name="specialisation" value={profileData.specialisation}
                        onChange={handleInputChange}
                        style={{ ...inputStyle(), paddingRight: 40 }}
                        placeholder="e.g. Cardiology, General Practice"
                      />
                    </Field>
                    <Field label="License Number" icon="uil-award">
                      <input
                        type="text" name="licenseNumber" value={profileData.licenseNumber}
                        onChange={handleInputChange}
                        style={{ ...inputStyle(), paddingRight: 40 }}
                        placeholder="Medical license number"
                      />
                    </Field>
                  </div>
                </div>

                {/* Actions */}
                <div style={{
                  paddingTop: 24,
                  borderTop: `1px solid ${C.outlineVariant}44`,
                  display: 'flex', justifyContent: 'flex-end', gap: 12,
                }}>
                  <button
                    type="button" onClick={handleReset}
                    style={{
                      padding: '11px 28px', borderRadius: 9999,
                      background: 'transparent',
                      border: `1px solid ${C.outlineVariant}`,
                      color: C.onSurfaceVariant, fontWeight: 700, fontSize: 14,
                      cursor: 'pointer', fontFamily: "'Inter',sans-serif",
                    }}
                  >Reset Changes</button>
                  <button
                    type="submit" disabled={isLoading}
                    style={{
                      padding: '11px 32px', borderRadius: 9999,
                      background: isLoading ? C.outline : C.primary,
                      color: '#fff', border: 'none',
                      fontWeight: 700, fontSize: 14, cursor: isLoading ? 'default' : 'pointer',
                      boxShadow: `0 4px 14px rgba(72,0,178,0.25)`,
                      fontFamily: "'Inter',sans-serif",
                      transition: 'all 0.15s',
                    }}
                  >
                    {isLoading ? 'Saving…' : 'Update Profile'}
                  </button>
                </div>
              </>
            )}
          </form>
        )}

        {/* ── APP ACCESS TAB ─────────────────────────────────── */}
        {activeTab === 'apps' && (
          <div style={{
            background: C.surfaceContainerLowest,
            borderRadius: 12, marginTop: 4, padding: '32px 28px',
          }}>
            <h3 style={{
              fontSize: 15, fontWeight: 700,
              fontFamily: "'Manrope',sans-serif",
              color: C.onSurface, marginBottom: 6,
              display: 'flex', alignItems: 'center', gap: 10,
            }}>
              <span style={{ width: 4, height: 24, background: C.primaryContainer, borderRadius: 4 }} />
              Application Access
            </h3>
            <p style={{ fontSize: 13, color: C.onSurfaceVariant, marginBottom: 24, marginLeft: 14 }}>
              These are the applications you currently have access to. Contact an administrator for additional access.
            </p>

            {isLoading ? (
              <div style={{ textAlign: 'center', padding: '40px 0', color: C.outline, fontSize: 13 }}>
                Loading app access…
              </div>
            ) : appAccess.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 0', color: C.outline }}>
                <i className="uil uil-apps" style={{ fontSize: 36, display: 'block', marginBottom: 8 }} />
                <p style={{ margin: 0, fontSize: 13 }}>No app access information available.</p>
              </div>
            ) : (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                gap: 14,
              }}>
                {appAccess.map((app) => (
                  <div key={app.appKey} style={{
                    background: C.surfaceContainerLow,
                    border: `1px solid ${app.isEnabled ? C.outlineVariant + '44' : C.outlineVariant + '22'}`,
                    borderRadius: 14, padding: '18px 16px',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                      <span style={{ fontSize: 26 }}>{app.icon}</span>
                      <span style={{
                        fontSize: 10, fontWeight: 700, padding: '3px 9px',
                        borderRadius: 9999,
                        background: app.isEnabled ? '#dcfce7' : C.surfaceContainerHigh,
                        color: app.isEnabled ? '#16a34a' : C.outline,
                        textTransform: 'uppercase', letterSpacing: 0.8,
                      }}>
                        {app.isEnabled ? 'Active' : 'Disabled'}
                      </span>
                    </div>
                    <div style={{ fontWeight: 700, fontSize: 13, color: C.onSurface, marginBottom: 4 }}>{app.appName}</div>
                    <div style={{ fontSize: 11, color: C.onSurfaceVariant, lineHeight: 1.5 }}>{app.description}</div>
                    {app.grantedAt && (
                      <div style={{ fontSize: 10, color: C.outline, marginTop: 8 }}>
                        Granted: {new Date(app.grantedAt).toLocaleDateString()}
                      </div>
                    )}
                    {app.expiresAt && (
                      <div style={{ fontSize: 10, color: '#b45309', marginTop: 2 }}>
                        Expires: {new Date(app.expiresAt).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div style={{
              marginTop: 24, padding: '14px 18px',
              background: `${C.primaryContainer}0d`,
              border: `1px solid ${C.primaryContainer}22`,
              borderRadius: 12, display: 'flex', alignItems: 'flex-start', gap: 12,
            }}>
              <i className="uil uil-info-circle" style={{ fontSize: 18, color: C.primaryContainer, flexShrink: 0, marginTop: 1 }} />
              <p style={{ margin: 0, fontSize: 12, color: C.onSurfaceVariant, lineHeight: 1.7 }}>
                <strong style={{ color: C.onSurface }}>Need additional access? </strong>
                If you need access to additional applications or have questions about your current access, please contact your system administrator.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* ── Clinical Intelligence Banner ─────────────────────── */}
      <div style={{
        position: 'relative', overflow: 'hidden',
        borderRadius: 18, background: '#22005d',
        padding: '32px 32px',
        display: 'flex', flexWrap: 'wrap',
        alignItems: 'center', justifyContent: 'space-between',
        gap: 24, marginTop: 24,
      }}>
        {/* Background glow */}
        <div style={{
          position: 'absolute', right: -80, top: -80,
          width: 256, height: 256,
          background: '#6200ee', borderRadius: '50%',
          filter: 'blur(100px)', opacity: 0.2,
          pointerEvents: 'none',
        }} />
        <div style={{ position: 'relative', zIndex: 1, maxWidth: 500 }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: 'rgba(255,255,255,0.1)',
            borderRadius: 9999, padding: '4px 12px',
            fontSize: 9, fontWeight: 700, letterSpacing: 2,
            textTransform: 'uppercase', color: 'rgba(255,255,255,0.8)',
            marginBottom: 12,
          }}>
            <i className="uil uil-star" style={{ fontSize: 11 }} />
            AI Health Verification
          </div>
          <h3 style={{
            fontSize: 20, fontWeight: 800, color: '#fff',
            fontFamily: "'Manrope',sans-serif",
            margin: '0 0 8px', fontStyle: 'italic',
          }}>
            Get verified for faster diagnostics.
          </h3>
          <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', margin: 0, lineHeight: 1.6 }}>
            Completing your medical history allows Dr Sam (our AI agent) to pre-analyse your condition before your live video consultation starts.
          </p>
        </div>
        <div style={{ position: 'relative', zIndex: 1 }}>
          <Link to="/patient-profile" style={{
            display: 'inline-flex', alignItems: 'center', gap: 10,
            background: '#fff', color: '#22005d',
            padding: '14px 24px', borderRadius: 12,
            fontWeight: 700, fontSize: 14, textDecoration: 'none',
            fontFamily: "'Manrope',sans-serif",
            boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
            transition: 'transform 0.15s',
          }}>
            Complete Health Profile
            <i className="uil uil-arrow-right" style={{ fontSize: 18 }} />
          </Link>
        </div>
      </div>

    </div>
  )
}